import type { SigningField } from '../types/fields';
import { isTextBasedFieldType, resolveFieldTextStyle } from './fontMapping';
import { measureTextFieldPdfSize } from './textFieldMeasure';
import { fieldTextLayoutMode, formLineBaselineOffsetFromBottom } from './textLayout';

export type TextFormatPatch = Partial<
  Pick<
    SigningField,
    | 'fontFamily'
    | 'bold'
    | 'italic'
    | 'underline'
    | 'strikethrough'
    | 'align'
    | 'color'
    | 'highlightColor'
    | 'lineHeight'
    | 'letterSpacing'
    | 'fontSize'
  >
>;

export const RESET_TEXT_FORMATTING: TextFormatPatch = {
  fontFamily: undefined,
  bold: undefined,
  italic: undefined,
  underline: undefined,
  strikethrough: undefined,
  align: undefined,
  color: undefined,
  highlightColor: undefined,
  lineHeight: undefined,
  letterSpacing: undefined,
};

export function toggleBooleanField(
  field: SigningField,
  key: 'bold' | 'italic' | 'underline' | 'strikethrough'
): TextFormatPatch {
  const current = !!field[key];
  return { [key]: !current };
}

export function applyFormatToFields(
  fields: SigningField[],
  ids: string[],
  patch: TextFormatPatch
): SigningField[] {
  const idSet = new Set(ids);
  return fields.map((f) => (idSet.has(f.id) && isTextBasedFieldType(f.type) ? { ...f, ...patch } : f));
}

const TEXT_METRIC_KEYS = [
  'fontSize',
  'bold',
  'italic',
  'fontFamily',
  'letterSpacing',
  'lineHeight',
] as const satisfies readonly (keyof TextFormatPatch)[];

function patchAffectsTextMetrics(patch: TextFormatPatch): boolean {
  return TEXT_METRIC_KEYS.some((key) => key in patch);
}

/** Merge format patch and resize the field box when text metrics change. */
export function buildTextFormatUpdates(
  field: SigningField,
  patch: TextFormatPatch
): Partial<SigningField> {
  const updates: Partial<SigningField> = { ...patch };
  if (patchAffectsTextMetrics(patch) && isTextBasedFieldType(field.type)) {
    const merged = { ...field, ...updates } as SigningField;
    const tight = measureTextFieldPdfSize(merged, merged.value || '');
    updates.width = tight.width;
    updates.height = tight.height;

    // For 'text' (which can be form-line or centered), keep the appropriate anchor
    // when metrics (e.g. fontSize) cause a size change.
    if (field.type === 'text' && updates.height != null) {
      const finalH = updates.height as number;
      const finalMode = fieldTextLayoutMode(field.type, { boxHeight: finalH, value: merged.value });
      const oldTop = field.y + (field.height || 0);
      if (finalMode === 'form-line') {
        const fs = merged.fontSize ?? 13;
        const sty = resolveFieldTextStyle(merged, finalMode);
        const fam = sty.metricsFamily || 'helvetica';
        const offset = formLineBaselineOffsetFromBottom(fs, fam);
        (updates as any).y = Math.max(0, Math.round(oldTop - offset));
      } else {
        (updates as any).y = Math.max(0, Math.round(oldTop - finalH));
      }
    }
  }
  return updates;
}

export function textFormatTargetIds(
  selectedFieldId: string | null,
  bulkIds: string[],
  fields: SigningField[]
): string[] {
  if (bulkIds.length > 0) {
    return bulkIds.filter((id) => {
      const f = fields.find((x) => x.id === id);
      return f && isTextBasedFieldType(f.type);
    });
  }
  if (selectedFieldId) {
    const f = fields.find((x) => x.id === selectedFieldId);
    if (f && isTextBasedFieldType(f.type)) return [selectedFieldId];
  }
  return [];
}